import { Modal } from "antd";
import React from "react";
import styles from "./BaseModal.module.css";

export interface BaseModalProps {
  open: boolean;
  onCancel: () => void;
  width?: number;
  className?: string;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  iconWithBorder?: boolean;
}

/**
 * BaseModal - Reusable modal component with consistent styling
 *
 * @example
 * ```tsx
 * <BaseModal
 *   open={isOpen}
 *   onCancel={handleCancel}
 *   title="Edit Item"
 *   subtitle="Edit your item details"
 *   icon={<EditIcon />}
 *   footer={
 *     <>
 *       <Button onClick={handleCancel}>Cancel</Button>
 *       <Button type="primary" onClick={handleSave}>Save</Button>
 *     </>
 *   }
 * >
 *   <Form>...</Form>
 * </BaseModal>
 * ```
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onCancel,
  width = 640,
  className = "",
  title,
  subtitle,
  icon,
  children,
  footer,
  loading = false,
  iconWithBorder = true,
}) => {
  const modalClassName = `${styles["base-modal"]} ${className}`.trim();

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={width}
      centered
      className={modalClassName}
      styles={{
        body: { padding: 0 },
        content: { borderRadius: 12 },
      }}
      maskClosable={!loading}
      closable={!loading}
    >
      <div className={styles["modal-content"]}>
        {/* Header */}
        {(title || subtitle || icon) && (
          <div className={styles["modal-header"]}>
            <div className={styles["header-left"]}>
              {icon &&
                (iconWithBorder ? (
                  <div className={styles["icon-container"]}>{icon}</div>
                ) : (
                  icon
                ))}
              {(title || subtitle) && (
                <div className={styles["title-section"]}>
                  {title && <h3 className={styles["modal-title"]}>{title}</h3>}
                  {subtitle && (
                    <p className={styles["modal-subtitle"]}>{subtitle}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        <div className={styles["modal-body"]}>{children}</div>

        {/* Footer */}
        {footer && <div className={styles["modal-footer"]}>{footer}</div>}
      </div>
    </Modal>
  );
};
